import { reactive } from 'vue'
import StorageService from '@/services/StorageService'
import { Network } from '@capacitor/network'
import ApiService from '@/services/ApiService'
import User from '@/store/User'

const key = 'school'

function dataModel () {
  return {
    // Seen school topics
    /** @type {Number[]} */
    topics: [],

    // Seen school topic pages
    /** @type {Number[]} */
    topic_pages: [],

    // Answered school questions
    /** @type {{ question_id: number, correct: boolean }[]} */
    school_questions: []
  }
}

const store = reactive({
  meta: {
    dirty: false
  },
  data: dataModel()
})

// Legacy PHP API used to send integer as string
function parseLegacyData (data = {}) {
  if (data.topics) {
    data.topics = data.topics.map(t => parseInt(t))
  }

  if (data.topic_pages) {
    data.topic_pages = data.topic_pages.map(t => parseInt(t))
  }

  return data
}

async function persist () {
  store.meta.dirty = true

  try {
    await School.sync()
  } catch (e) {
    console.warn('School: Failed to sync school progress', e)
  } finally {
    await StorageService.set(key, store)
  }
}

const School = {
  key,

  setStore (s = {}) {
    if (s.data) {
      s.data = parseLegacyData(s.data)
    }

    Object.assign(store.meta, s.meta)
    Object.assign(store.data, s.data)
  },

  isDirty () {
    return store.meta.dirty
  },

  async reset (d = {}) {
    const data = Object.assign(
      dataModel(),
      parseLegacyData(d)
    )
    this.setStore({ meta: { dirty: false }, data })
    await StorageService.set(key, store)
  },

  async sync () {
    const { connected } = await Network.getStatus()

    if (connected && store.meta.dirty) {
      const user = User.getUser()

      if (user) {
        try {
          const { data: resp } = await ApiService.updateSeenTopics(user.id, store.data.topics, store.data.topic_pages, store.data.school_questions)

          const data = {};

          ['seen_topics', 'seen_topic_pages', 'seen_school_questions'].forEach(key => {
            if (resp.user?.[key]) {
              data[key] = resp.user[key];
            }
          });

          this.setStore({ data })

          store.meta.dirty = false

          // Store dirty state change
          await StorageService.set(key, store)
        } catch (e) {
          console.warn('School: Failed to sync school progress', e)
        }
      }
    }
  },

  getTopics () {
    return store.data.topics
  },

  getTopicPages () {
    return store.data.topic_pages
  },

  /**
   *
   * @param {Number} id Original ID/code of the topic
   * @returns {Promise<void>}
   */
  completeTopic: async (id) => {
    if (!id || store.data.topics.includes(id)) return
    store.data.topics.push(id)

    await persist()
  },

  completeTopicPage: async (id) => {
    if (!id || store.data.topic_pages.includes(id)) return
    store.data.topic_pages.push(id)

    await persist()
  },

  isTopicCompleted (id) {
    return store.data.topics.includes(id)
  },

  isTopicPageCompleted (id) {
    return store.data.topic_pages.includes(id)
  },

  isQuestionAnswered (id) {
    const ids = store.data.school_questions.map(q => q.question_id)
    return ids.includes(id)
  },

  async answerQuestion(id, correct) {
    store.data.school_questions.push({ question_id: id, correct })
    await persist()
  }
}

export default School
